package dev.kuml.portal.web.legal

import dev.kuml.portal.web.components.languageSwitcher
import dev.kuml.portal.web.i18n.legal.LegalBlock
import dev.kuml.portal.web.i18n.legal.LegalPage
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.h1
import io.kvision.html.h2
import io.kvision.html.li
import io.kvision.html.link
import io.kvision.html.span
import io.kvision.html.ul
import kotlinx.browser.window

/**
 * The three static, unauthenticated legal pages (§ 5 TMG Impressum, Art. 13 DSGVO
 * Datenschutzerklärung, AGB) required for a service with real registration, real paying customers
 * (Lemon Squeezy, live since 2026-07-29), and an AI assistant that forwards data to external
 * providers. Reachable via [dev.kuml.portal.web.routing.Router]'s `/impressum` / `/datenschutz` /
 * `/agb` routes, linked from [dev.kuml.portal.web.components.legalLinks]. Mounted directly on the
 * root panel (no [dev.kuml.portal.web.components.portalHeader], which needs an authenticated
 * [dev.kuml.portal.web.api.PortalApi] call for the usage chip) -- all three pages must render for a
 * visitor who isn't logged in at all.
 *
 * R-18: content and structure now live in [dev.kuml.portal.web.i18n.legal.LegalPages]
 * (`LegalPagesDe`/`LegalPagesEn`) as a [LegalPage] of [LegalBlock]s -- this file is now a single,
 * language-independent RENDERER (`legalScreen`) plus three thin per-page entry points. See this
 * wave's plan §6.1 for why: the render logic can no longer drift between languages (there is only
 * one implementation of it), and each language's [LegalPage] carries its OWN [LegalPage.notice]
 * (the English pages' courtesy-translation disclaimer; `null` on every German page).
 */
public fun Container.impressumScreen() {
    legalScreen(t.legal.pages.imprint)
}

public fun Container.datenschutzScreen() {
    legalScreen(t.legal.pages.privacy)
}

public fun Container.agbScreen() {
    legalScreen(t.legal.pages.terms)
}

private fun Container.legalScreen(page: LegalPage) {
    div(className = "kportal-page kportal-prose") {
        div(className = "kportal-legal-page-head") {
            backLink()
            languageSwitcher()
        }
        h1(page.title)
        page.notice?.let { notice -> div(notice, className = "kportal-legal-notice") }
        for (block in page.blocks) {
            when (block) {
                is LegalBlock.Heading -> h2(block.text)
                is LegalBlock.Paragraph -> div(block.text)
                is LegalBlock.Rich ->
                    div {
                        for (segment in block.segments) {
                            when (segment) {
                                is LegalBlock.Rich.Segment.Text -> span(segment.text)
                                is LegalBlock.Rich.Segment.Link ->
                                    link(
                                        segment.text,
                                        segment.href,
                                        target = if (segment.newTab) "_blank" else null,
                                    ) {
                                        // A `target="_blank"` link keeps `window.opener` around
                                        // (tab-nabbing / reverse-tabnabbing risk) unless the
                                        // opener relationship is explicitly severed -- harmless
                                        // today since every current href is a static constant
                                        // (ec.europa.eu/apache.org/lfd.niedersachsen.de), but this
                                        // is the one central render site for every legal link, so
                                        // it's set here once rather than left to whichever future
                                        // dynamic href lands first.
                                        if (segment.newTab) setAttribute("rel", "noopener noreferrer")
                                    }
                            }
                        }
                    }
                is LegalBlock.Bullets -> ul { block.items.forEach { item -> li(item) } }
                is LegalBlock.Lines -> div { block.lines.forEach { line -> div(line) } }
            }
        }
    }
}

private fun Container.backLink() {
    button(t.legal.backLink, className = "kportal-legal-back").onClick { window.history.back() }
}
